/**
 * UTM parameter capture & retrieval.
 *
 * On first paint we read utm_source / utm_medium / utm_campaign from the URL
 * query string and persist them to sessionStorage so they survive client-side
 * navigation (e.g. landing page -> job application form).
 */

export interface StoredUtm {
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
}

const STORAGE_KEYS: Record<keyof StoredUtm, string> = {
  utmSource: "utm_source",
  utmMedium: "utm_medium",
  utmCampaign: "utm_campaign",
};

function safeSessionStorage(): Storage | null {
  // Guard for SSR and browsers where sessionStorage access throws
  // (e.g. Safari private mode, blocked third-party contexts).
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Read UTM params from the current URL and persist them to sessionStorage.
 * Only overwrites stored values when the param is present in the URL, so a
 * later UTM-less navigation doesn't clobber the original attribution.
 */
export function captureUtmParams(): void {
  if (typeof window === "undefined") return;
  const storage = safeSessionStorage();
  if (!storage) return;

  try {
    const params = new URLSearchParams(window.location.search);
    (Object.values(STORAGE_KEYS) as string[]).forEach((key) => {
      const value = params.get(key);
      if (value) {
        storage.setItem(key, value);
      }
    });
  } catch {
    // Never let attribution tracking break the app.
  }
}

/**
 * Retrieve previously captured UTM params from sessionStorage.
 */
export function getStoredUtm(): StoredUtm {
  const storage = safeSessionStorage();
  const read = (key: string): string | null => {
    if (!storage) return null;
    try {
      return storage.getItem(key);
    } catch {
      return null;
    }
  };

  return {
    utmSource: read(STORAGE_KEYS.utmSource),
    utmMedium: read(STORAGE_KEYS.utmMedium),
    utmCampaign: read(STORAGE_KEYS.utmCampaign),
  };
}
